import React, { useState, useEffect, useCallback } from 'react';
import ReactECharts from 'echarts-for-react';
import apiClient from '../../utils/axios';

const FETCH_TIMEOUT_MS = 15000; // 15 seconds timeout

export const IntegrationsStatsBar = () => {
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fetchKey, setFetchKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let isTimeout = false;

    const timeoutId = setTimeout(() => {
      isTimeout = true;
      controller.abort();
    }, FETCH_TIMEOUT_MS);

    const fetchStatistics = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiClient.get('/integrations/statistics/', {
          signal: controller.signal
        });

        clearTimeout(timeoutId);
        setOptions(response.data || {});
      } catch (err) {
        clearTimeout(timeoutId);
        // Only set error if it was a timeout, not a cleanup abort
        if (isTimeout) {
          setError('Request timed out. Please try again later.');
        } else if (!controller.signal.aborted) {
          setError('Failed to load statistics');
        }
      } finally {
        if (!controller.signal.aborted || isTimeout) {
          setLoading(false);
        }
      }
    };

    fetchStatistics();

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [fetchKey]);

  const handleRetry = useCallback(() => {
    setFetchKey(prev => prev + 1);
  }, []);

  // Loading placeholder
  if (loading) {
    return (
      <div className="dashboard-card stats-card">
        <div className="card-header">
          <h2>Integrations Statistics</h2>
        </div>
        <div className="stats-bar-placeholder">
          <div className="placeholder-chart">
            <div className="placeholder-bar" style={{ height: '60%' }}></div>
            <div className="placeholder-bar" style={{ height: '80%' }}></div>
            <div className="placeholder-bar" style={{ height: '45%' }}></div>
            <div className="placeholder-bar" style={{ height: '70%' }}></div>
            <div className="placeholder-bar" style={{ height: '55%' }}></div>
          </div>
          <div className="placeholder-loading">
            <div className="loading-spinner-small"></div>
            <span>Loading statistics...</span>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="dashboard-card stats-card">
        <div className="card-header">
          <h2>Integrations Statistics</h2>
        </div>
        <div className="stats-bar-error">
          <div className="error-icon">⚠️</div>
          <div className="error-text">{error}</div>
          <button className="retry-btn" onClick={handleRetry}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Success state - render chart
  return (
    <div className="dashboard-card stats-card">
      <div className="card-header">
        <h2>Integrations Statistics</h2>
      </div>
      <ReactECharts option={options} />
    </div>
  );
};

export default IntegrationsStatsBar;
